// @catalog F.030
// TabHistorico — Tabla completa de histórico mensual. Reemplaza la
// alerta del dialog viejo con un banner separador in-line en el punto de
// migración Odoo 16 → Odoo 18.

import { Box, Typography } from "@mui/material";
import { Fragment } from "react";
import type { MonthlySale } from "../productDetailTypes";
import { fmtCurrencyK, fmtNum, fmtPct, formatMonthLabel } from "./helpers";

interface TabHistoricoProps {
  sales: MonthlySale[];
}

const headerCellSx = {
  textAlign: "left" as const,
  fontWeight: 500,
  fontSize: 11.5,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  color: "var(--ink-3)",
  py: 1.25,
  px: 1.5,
  borderBottom: "1px solid var(--border)",
  bgcolor: "var(--bg)",
};

const bodyCellSx = {
  py: 1.25,
  px: 1.5,
  borderBottom: "1px solid var(--border)",
  color: "var(--ink-1)",
  fontVariantNumeric: "tabular-nums" as const,
  fontSize: 13,
};

export function TabHistorico({ sales }: TabHistoricoProps) {
  if (sales.length === 0) {
    return (
      <Box
        sx={{
          py: 4,
          textAlign: "center",
          color: "var(--ink-3)",
          fontSize: 13,
        }}
      >
        Sin historial mensual.
      </Box>
    );
  }

  // Reversed: meses más recientes arriba
  const reversed = [...sales].reverse();
  // Encontrar índice donde aparece el primer dato Odoo 16 al recorrer arriba→abajo
  const migrationIdx = reversed.findIndex((s) => s.dataSource === "odoo16");

  return (
    <Box
      sx={{
        bgcolor: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 1.25,
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: "var(--ink-3)",
          fontWeight: 500,
          letterSpacing: "0.02em",
          px: 2,
          pt: 1.75,
        }}
      >
        Histórico mensual completo
      </Typography>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          mt: 1,
        }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={headerCellSx}>
              Mes
            </Box>
            <Box component="th" sx={{ ...headerCellSx, textAlign: "right" }}>
              Piezas
            </Box>
            <Box component="th" sx={{ ...headerCellSx, textAlign: "right" }}>
              Ventas
            </Box>
            <Box component="th" sx={{ ...headerCellSx, textAlign: "right" }}>
              Costo
            </Box>
            <Box component="th" sx={{ ...headerCellSx, textAlign: "right" }}>
              Margen
            </Box>
            <Box component="th" sx={{ ...headerCellSx, textAlign: "right" }}>
              % Margen
            </Box>
            <Box component="th" sx={headerCellSx}>
              Fuente
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {reversed.map((s, i) => {
            const showMigration = i === migrationIdx;
            const marginPct =
              s.grossSales > 0 ? (s.margin / s.grossSales) * 100 : 0;
            const isOdoo18 = s.dataSource === "odoo18";
            return (
              <Fragment key={s.monthKey}>
                {showMigration && (
                  <Box component="tr">
                    <Box
                      component="td"
                      colSpan={7}
                      sx={{
                        py: 1,
                        px: 1.5,
                        bgcolor: "var(--warn-soft)",
                        color: "var(--warn)",
                        fontSize: 11.5,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderTop: "1px solid var(--warn)",
                        borderBottom: "1px solid var(--warn)",
                      }}
                    >
                      ↓ Datos de Odoo 16 (sistema anterior) ↓
                    </Box>
                  </Box>
                )}
                <Box
                  component="tr"
                  sx={{
                    "&:hover": { bgcolor: "var(--bg)" },
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      ...bodyCellSx,
                      fontWeight: 500,
                    }}
                  >
                    {formatMonthLabel(s.monthKey)}
                  </Box>
                  <Box
                    component="td"
                    sx={{ ...bodyCellSx, textAlign: "right" }}
                  >
                    {fmtNum(s.unitsSold)}
                  </Box>
                  <Box
                    component="td"
                    sx={{ ...bodyCellSx, textAlign: "right" }}
                  >
                    {fmtCurrencyK(s.grossSales)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      ...bodyCellSx,
                      textAlign: "right",
                      color: "var(--ink-3)",
                    }}
                  >
                    {fmtCurrencyK(s.cost)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      ...bodyCellSx,
                      textAlign: "right",
                      color: s.margin < 0 ? "var(--neg)" : "var(--ink-1)",
                      fontWeight: 500,
                    }}
                  >
                    {fmtCurrencyK(s.margin)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      ...bodyCellSx,
                      textAlign: "right",
                      color: "var(--ink-3)",
                    }}
                  >
                    {fmtPct(marginPct)}
                  </Box>
                  <Box component="td" sx={bodyCellSx}>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 0.875,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontSize: 10,
                        fontWeight: 500,
                        bgcolor: isOdoo18 ? "var(--pos-soft)" : "var(--bg-2)",
                        color: isOdoo18 ? "var(--pos)" : "var(--ink-2)",
                        border: isOdoo18 ? "none" : "1px solid var(--border)",
                      }}
                    >
                      {isOdoo18 ? "18" : "16"}
                    </Box>
                  </Box>
                </Box>
              </Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default TabHistorico;
